import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";

function extractEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  // Vimeo
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  // Loom
  const lm = url.match(/loom\.com\/share\/([\w-]+)/);
  if (lm) return `https://www.loom.com/embed/${lm[1]}`;
  return url;
}

function CaseCard({ c }) {
  const [values, setValues] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const parsed = {};
    for (const f of c.fields) {
      const raw = values[f.id];
      if (raw === undefined || raw === "") { toast.error("Fill all fields"); return; }
      parsed[f.id] = parseFloat(String(raw).replace(/[,$\s]/g, ""));
    }
    setSubmitting(true);
    try {
      const r = await api.post("/practice/submit", { case_id: c.id, answers: parsed });
      setFeedback(r.data);
      if (r.data.score === 100) toast.success("Perfect return!");
      else toast.info(`Score: ${r.data.score}% — review the highlighted lines.`);
    } catch (e) { toast.error("Submission failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <Card className="p-6 lg:p-8" data-testid={`practice-case-${c.id}`}>
      {c.video_url && extractEmbedUrl(c.video_url) && (
        <div className="mb-6 aspect-video w-full overflow-hidden rounded-md border border-border bg-black" data-testid={`practice-video-${c.id}`}>
          <iframe
            src={extractEmbedUrl(c.video_url)}
            title={`${c.title} walkthrough`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
      {!c.video_url && (
        <div className="mb-6 rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          Instructor video coming soon — the admin can add a YouTube, Vimeo, or Loom URL from the Branding panel.
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="label-caps mb-2">Case study</div>
          <h3 className="font-serif text-3xl leading-tight mb-4">{c.title}</h3>
          <p className="text-sm leading-relaxed mb-6">{c.scenario}</p>
          <div className="label-caps mb-3">Given data</div>
          <div className="space-y-1.5 text-sm border-l-2 border-border pl-4">
            {c.given_data.map((g, i) => (
              <div key={i} className="flex justify-between font-mono text-xs">
                <span className="text-muted-foreground">{g.label}</span>
                <span>{g.value}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="label-caps mb-3">Your 1040 entries</div>
          <div className="space-y-3">
            {c.fields.map((f) => {
              const fb = feedback?.feedback.find(x => x.field_id === f.id);
              return (
                <div key={f.id}>
                  <Label className="text-xs mb-1 block">{f.label}</Label>
                  {f.hint && <div className="text-[10px] font-mono text-muted-foreground mb-1">Hint: {f.hint}</div>}
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid={`practice-input-${c.id}-${f.id}`}
                      className={`tax-input ${fb ? (fb.correct ? "border-primary" : "border-destructive") : ""}`}
                      value={values[f.id] ?? ""}
                      onChange={e => setValues(v => ({ ...v, [f.id]: e.target.value }))}
                      placeholder="0"
                      disabled={!!feedback}
                    />
                    {fb && (fb.correct ? <CheckCircle2 size={16} className="text-primary" /> : <XCircle size={16} className="text-destructive" />)}
                  </div>
                  {fb && !fb.correct && (
                    <div className="text-xs text-muted-foreground mt-1">Correct: <span className="font-mono">{fb.correct_answer}</span></div>
                  )}
                </div>
              );
            })}
          </div>
          {!feedback ? (
            <Button className="mt-6 w-full" onClick={submit} disabled={submitting} data-testid={`practice-submit-${c.id}`}>
              {submitting ? "Checking…" : "Check my return"}
            </Button>
          ) : (
            <div className="mt-6">
              <div className="label-caps mb-2">Result</div>
              <div className="font-serif text-4xl mb-3" data-testid={`practice-score-${c.id}`}>{feedback.score}%</div>
              <Button variant="outline" size="sm" onClick={() => { setFeedback(null); setValues({}); }}>Reset</Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function Practice() {
  const [cases, setCases] = useState([]);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    api.get("/practice").then(r => setCases(r.data)).catch(e => {
      if (e?.response?.status === 402) setLocked(true);
    });
  }, []);

  if (locked) return (
    <Layout>
      <div className="max-w-2xl mx-auto p-6 lg:p-12">
        <div className="border border-accent rounded-md p-10 bg-accent/5 text-center" data-testid="practice-paywall">
          <div className="label-caps mb-2 text-accent">Practice locked</div>
          <h1 className="font-serif text-4xl lg:text-5xl mb-4">Enroll to work real 1040s.</h1>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">Interactive practice returns unlock with any enrollment plan.</p>
          <Button asChild size="lg" data-testid="practice-see-plans">
            <a href="/pricing">See plans</a>
          </Button>
        </div>
      </div>
    </Layout>
  );

  if (!cases.length) return <Layout><div className="p-10">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 lg:p-12">
        <div className="label-caps mb-3">Live practice</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-6">1040 Practice Returns</h1>
        <p className="text-muted-foreground max-w-2xl mb-10">Work through four real scenarios. Each field is checked with a $2 tolerance for rounding.</p>

        <Tabs defaultValue={cases[0].id} className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto">
            {cases.map(c => <TabsTrigger key={c.id} value={c.id} data-testid={`practice-tab-${c.id}`}>{c.title.split("—")[0].trim()}</TabsTrigger>)}
          </TabsList>
          {cases.map(c => (
            <TabsContent key={c.id} value={c.id}>
              <CaseCard c={c} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Layout>
  );
}
